import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardBody, Spinner, Button } from '@patternfly/react-core';
import { ToggleBtn } from '@app/components/ToggleBtn';
import { usePageViewStats } from '../hooks/useUsageAnalytics';
import { DAYS_OPTIONS } from '../types';

const CHART_COLOR = '#6753ac';

/** Convert DB page key (e.g. ENVIRONMENT_DASHBOARD) to readable label. */
function pageLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

function exportCsv(rows: { page: string; count: number }[]) {
  const header = 'Page,Visits\n';
  const body = rows.map((r) => `"${pageLabel(r.page)}",${r.count}`).join('\n');
  const blob = new Blob([header + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `page-views-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  cluster?: string | null;
}

export const PageViewsChart: React.FC<Props> = ({ cluster }) => {
  const [days, setDays] = useState<number>(7);
  const { data, isLoading, isFetching, error } = usePageViewStats(days, cluster);

  const chartData = data.map((r) => ({ page: pageLabel(r.page), count: r.count }));

  return (
    <Card>
      <CardHeader
        actions={{
          actions: chartData.length > 0 ? (
            <Button variant="link" isInline onClick={() => exportCsv(data)}>
              ↓ CSV
            </Button>
          ) : undefined,
        }}
      >
        <CardTitle>Page Views</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="ua-controls">
          <div className="ua-btn-group">
            {DAYS_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={days === o.value} onClick={() => setDays(o.value)} variant="purple" />
            ))}
          </div>
          {isFetching && <Spinner size="sm" />}
        </div>

        {error && <div className="ua-error">Error: {error}</div>}

        {isLoading ? (
          <div className="ua-empty-state"><Spinner /></div>
        ) : chartData.length === 0 ? (
          <div className="ua-empty-state">
            No page view data for the selected period.
            <div className="ua-empty-state__hint">
              Page views are tracked automatically when users navigate. Data appears once users visit pages.
            </div>
          </div>
        ) : (
          <div className="ua-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 4, right: 40, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="none" stroke="#f3f4f6" horizontal={false} />
                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="page"
                  width={200}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12, fill: '#151515' }}
                />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #d2d2d2', borderRadius: 4, fontSize: 12 }}
                  formatter={(v: number) => [v.toLocaleString(), 'Visits']}
                  cursor={{ fill: '#f3f4f6' }}
                />
                <Bar dataKey="count" radius={[0, 3, 3, 0]} name="Visits">
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLOR} fillOpacity={1 - i * 0.04} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
